/*!
 * Internal-search adapter for AMOUR Parfums theme.
 *
 * Augments Shopify's built-in predictive-search dropdown with results
 * for products that are hidden from the search engine via the seo.hidden
 * metafield. The hidden products live in a smart collection (handle below)
 * and are exposed as JSON via templates/collection.search-index.liquid.
 *
 * The script lazy-loads on first user interaction with the search input.
 */
(function () {
  'use strict';

  var INTERNAL_COLLECTION_HANDLE = 'int-idx-xyz';
  var INDEX_URL = '/collections/' + INTERNAL_COLLECTION_HANDLE + '?view=search-index';
  var SEARCH_INPUT_SELECTOR = '.search__input';
  var DROPDOWN_SELECTOR = '#predictive-search';
  var MIN_QUERY_LENGTH = 2;
  var MAX_RESULTS = 8;
  var DEBOUNCE_MS = 220;
  var INTERNAL_FLAG = 'data-internal-search-id';

  var indexPromise = null;
  var miniSearch = null;
  var products = [];
  var lastQuery = '';
  var observers = [];

  function loadIndex() {
    if (indexPromise) return indexPromise;
    if (typeof MiniSearch === 'undefined') {
      return Promise.reject(new Error('MiniSearch not loaded'));
    }
    indexPromise = fetch(INDEX_URL, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('Index HTTP ' + r.status);
        return r.text();
      })
      .then(function (text) {
        products = JSON.parse(text);
        miniSearch = new MiniSearch({
          idField: 'id',
          fields: [
            'title', 'vendor', 'type', 'tags', 'fragrance',
            'dek_vendor', 'product_no', 'gender', 'description'
          ],
          storeFields: ['id'],
          searchOptions: {
            boost: { title: 3, fragrance: 2.5, dek_vendor: 2, product_no: 2, vendor: 1.5 },
            prefix: true,
            fuzzy: 0.2,
            combineWith: 'AND'
          },
          extractField: function (doc, field) {
            var v = doc[field];
            if (v == null) return '';
            if (Array.isArray(v)) return v.join(' ');
            return String(v);
          }
        });
        miniSearch.addAll(products);
        return products;
      })
      .catch(function (err) {
        if (window.console) console.warn('[internal-search] index load failed', err);
        indexPromise = null;
        throw err;
      });
    return indexPromise;
  }

  function search(query) {
    if (!miniSearch || !query) return [];
    var hits = miniSearch.search(query);
    var byId = Object.create(null);
    products.forEach(function (p) { byId[p.id] = p; });
    return hits
      .map(function (h) { return byId[h.id]; })
      .filter(Boolean);
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  var PERFUME_TYPES = ['Perfumes', 'Parfumi', 'Parfümök', 'Parfemi', 'Parfüme', 'Parfums'];

  function getPerfumeLabel() {
    return (window.theme && theme.strings && theme.strings.perfumeLabel) ||
           'Parfum, navdihnjen po:';
  }

  function renderPredictiveCard(p) {
    var labelHtml;
    if (PERFUME_TYPES.indexOf(p.type) !== -1) {
      var prefix = '';
      if (p.dek_vendor && p.dek_vendor.indexOf('AMOUR PARFUMS') === -1) {
        prefix = getPerfumeLabel() + ' ' + p.dek_vendor + ' -';
      }
      labelHtml =
        '<span>' + escapeHtml(prefix + (p.fragrance || '')) + '</span>' +
        '<span class="gender-field">' +
        escapeHtml(p.gender || '') + ' - ' + escapeHtml((p.product_no || '') + ' ') +
        '</span>';
    } else {
      labelHtml = '<span>' + escapeHtml(p.title) + '</span>';
    }

    var imageHtml = p.image
      ? '<img src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="80" height="80">'
      : '';

    var inspiredHtml = '';
    if (p.inspired_by) {
      var shopDomain = (window.theme && theme.shop && theme.shop.permanentDomain) || '';
      var fallback = p.image || '';
      var smallSrc = p.image_small || p.image || '';
      inspiredHtml =
        '<div class="product-inspired-by search-result-inspired">' +
          '<img ' +
            'src="' + escapeHtml(smallSrc) + '" ' +
            'loading="lazy" ' +
            'width="300" ' +
            'height="300" ' +
            'data-inspired-product-id="' + escapeHtml(String(p.id)) + '" ' +
            'data-inspired-shop="' + escapeHtml(shopDomain) + '" ' +
            'data-inspired-width="300" ' +
            'data-inspired-fallback="' + escapeHtml(fallback) + '"' +
          '>' +
        '</div>';
    }

    return (
      '<li role="option" ' + INTERNAL_FLAG + '="' + p.id + '">' +
        '<a href="' + escapeHtml(p.url) + '">' +
          '<div class="results-products__image grid__image-ratio ffff">' +
            imageHtml +
            inspiredHtml +
          '</div>' +
          '<div class="results-products__info gggg">' +
            labelHtml +
          '</div>' +
        '</a>' +
      '</li>'
    );
  }

  function getDropdowns() {
    return Array.prototype.slice.call(document.querySelectorAll(DROPDOWN_SELECTOR));
  }

  function clearInjections(dropdown) {
    var nodes = dropdown.querySelectorAll('[' + INTERNAL_FLAG + ']');
    Array.prototype.forEach.call(nodes, function (n) { n.remove(); });
    var emptySection = dropdown.querySelector('.results--products[data-internal-search-section]');
    if (emptySection) emptySection.remove();
  }

  function injectInto(dropdown, query) {
    if (!dropdown) return;
    clearInjections(dropdown);

    var matches = search(query).slice(0, MAX_RESULTS);
    if (!matches.length) return;

    var html = matches.map(renderPredictiveCard).join('');
    var existingList = dropdown.querySelector('.results--products ul');

    if (existingList) {
      existingList.insertAdjacentHTML('beforeend', html);
      return;
    }

    var resultsRoot =
      dropdown.querySelector('.results__group-2') ||
      dropdown.querySelector('.predictive-search-results') ||
      dropdown;

    if (!resultsRoot) return;

    var noneEl = dropdown.querySelector('.predictive-search-results--none');
    if (noneEl) {
      noneEl.classList.remove('predictive-search-results--none');
    }
    var noResultsLink = dropdown.querySelector('.predictive-search__no-results');
    if (noResultsLink) {
      var li = noResultsLink.closest('.results') || noResultsLink.parentNode;
      if (li) li.remove();
    }

    var section = document.createElement('div');
    section.className = 'results results--products';
    section.setAttribute('data-internal-search-section', '1');
    var heading = (window.theme && theme.strings && theme.strings.searchProducts) || 'Izdelki';
    section.innerHTML =
      '<h3 class="h4">' + escapeHtml(heading) + '</h3>' +
      '<ul role="listbox">' + html + '</ul>';
    resultsRoot.appendChild(section);
  }

  function injectAll(query) {
    getDropdowns().forEach(function (d) { injectInto(d, query); });
  }

  function attachObserver(dropdown) {
    if (!dropdown || dropdown._internalSearchObserved) return;
    dropdown._internalSearchObserved = true;
    var ob = new MutationObserver(function (mutations) {
      var hasExternalChange = mutations.some(function (m) {
        return Array.prototype.some.call(m.addedNodes, function (n) {
          if (n.nodeType !== 1) return false;
          if (n.hasAttribute && n.hasAttribute(INTERNAL_FLAG)) return false;
          if (n.getAttribute && n.getAttribute('data-internal-search-section') === '1') return false;
          return true;
        });
      });
      if (hasExternalChange && lastQuery) {
        injectInto(dropdown, lastQuery);
      }
    });
    ob.observe(dropdown, { childList: true, subtree: true });
    observers.push(ob);
  }

  function attachObservers() {
    getDropdowns().forEach(attachObserver);
  }

  function handleInputEvent(input) {
    if (input._internalSearchBound) return;
    input._internalSearchBound = true;

    var debounceTimer = null;
    input.addEventListener('input', function () {
      var q = input.value.trim();
      lastQuery = q;
      clearTimeout(debounceTimer);

      if (q.length < MIN_QUERY_LENGTH) {
        getDropdowns().forEach(clearInjections);
        return;
      }

      debounceTimer = setTimeout(function () {
        loadIndex()
          .then(function () {
            attachObservers();
            injectAll(q);
          })
          .catch(function () { /* already logged */ });
      }, DEBOUNCE_MS);
    });

    input.addEventListener('focus', function () {
      // warm the index on first focus to remove perceived latency
      if (!indexPromise) loadIndex().catch(function () {});
    });
  }

  function bindInputs() {
    Array.prototype.forEach.call(
      document.querySelectorAll(SEARCH_INPUT_SELECTOR),
      handleInputEvent
    );
  }

  function init() {
    bindInputs();
    attachObservers();

    // Watch for predictive-search elements that get injected later
    // (e.g. when the mobile search drawer mounts on first open).
    var bodyObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          var hasMatch =
            (typeof n.matches === 'function' &&
              (n.matches('predictive-search') || n.matches(SEARCH_INPUT_SELECTOR))) ||
            (typeof n.querySelector === 'function' &&
              (n.querySelector('predictive-search') || n.querySelector(SEARCH_INPUT_SELECTOR)));
          if (hasMatch) {
            bindInputs();
            attachObservers();
            return;
          }
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.internalSearch = {
    loadIndex: loadIndex,
    search: search,
    products: function () { return products.slice(); }
  };
})();
